import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import { userLikeApi } from "@/react/api/userLikeApi";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { commentLikeInvalidations } from "@/react/lib/queries/commentLikes";
import { commentCache } from "@/react/lib/queries/comments";
import type { Comment } from "@/react/types/Comment";

interface UseLikeCommentParams {
  comment: Comment;
}

export const useLikeComment = ({ comment }: UseLikeCommentParams) => {
  const { id, post_id: postId, is_liked: isLiked } = comment;
  const { error } = useToast();

  const updateLikesInCache = (nextLiked: boolean) => {
    commentCache.update({
      postId,
      id,
      updater: current => ({
        ...current,
        is_liked: nextLiked,
        likes_count: current.likes_count + (nextLiked ? 1 : -1),
      }),
    });
  };

  const { mutate, isPending } = useMutation<
    unknown,
    unknown,
    { nextLiked: boolean }
  >({
    mutationFn: ({ nextLiked }) =>
      (nextLiked ? userLikeApi.create : userLikeApi.destroy)({
        userLikeableId: id,
        userLikableType: "Comment",
        forReactQuery: true,
      }),
    onMutate: ({ nextLiked }) => updateLikesInCache(nextLiked),
    onError: (_err, { nextLiked }) => {
      updateLikesInCache(!nextLiked);
      error(t("comments.like.alerts.failure"));
    },
    onSettled: () => commentLikeInvalidations.list(id),
  });

  const toggleLike = () => {
    if (isPending) return;
    mutate({ nextLiked: !isLiked });
  };

  return { toggleLike, isPending };
};
